import { NextSeo } from "next-seo";
import SimpleFooter from "components/SimpleFooter";
import SimpleNavbar from "components/SimpleNavbar";
import Link from "next/link";

const TermsAndConditions = () => {
  return (
    <>
      <NextSeo title="Teamshares | Terms of Use" />
      <div className="min-h-screen antialiased leading-normal text-gray-900 bg-white">
        <SimpleNavbar />
        <main>
          <div className="max-w-6xl px-12 mx-auto text-sm lg:text-lg">
            <div className="self-center mb-10 text-3xl font-bold pt-28 md:text-4xl">
              Teamshares Terms of Use
            </div>
            <div className="mb-2 font-bold lg:mb-4">
              Last Updated:&nbsp;
              <span className="font-normal">09/20/2023</span>
            </div>
            <div className="mb-2 lg:mb-4">
              Welcome, and thank you for your interest in Teamshares Inc. (
              <span className="font-bold">“Teamshares,” “we,” “our,” </span>
              or
              <span className="font-bold"> “us”</span>) and our website at
              www.teamshares.com, including subdomains of that website
              (collectively, the
              <span className="font-bold"> “Site”</span>
              ). These Terms of Use are a legally binding contract between you
              and Teamshares regarding your use of the Site.
            </div>
            <div className="mb-2 font-bold lg:mb-4">
              PLEASE READ THE FOLLOWING TERMS CAREFULLY.
            </div>
            <div>
              <span className="font-bold">
                BY AGREEING TO, ACCESSING, OR USING THE SITE
              </span>
              , YOU AGREE THAT YOU HAVE READ AND UNDERSTOOD, AND, AS A CONDITION
              TO YOUR ACCESS TO OR USE OF THE SITE, YOU AGREE TO BE BOUND BY,
              THE FOLLOWING TERMS AND CONDITIONS, INCLUDING THE{" "}
              <Link href="/privacy-policy" target="_blank" rel="noopener">
                PRIVACY POLICY
              </Link>{" "}
              (DEFINED BELOW) (TOGETHER, THESE{" "}
              <span className="font-bold">“TERMS”</span>). IF YOU ARE NOT
              ELIGIBLE, OR DO NOT AGREE TO THE TERMS, THEN YOU DO NOT HAVE OUR
              PERMISSION TO USE THE SITE. YOUR USE OF THE SITE, AND TEAMSHARES’S
              PROVISION OF THE SITE TO YOU, CONSTITUTES AN AGREEMENT BY
              TEAMSHARES AND BY YOU TO BE BOUND BY THESE TERMS.
            </div>
            <ol className="list-decimal">
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">Overview.</span>
                  The Site provides you with information about the Teamshares
                  service offerings.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">Eligibility.</span>
                  By accessing or using this Site, you represent and warrant to
                  us that your use of the Site is in compliance with any and all
                  applicable laws and regulations. If you are an entity,
                  organization, or company, the individual accessing or using
                  this Site on your behalf represents and warrants that they
                  have authority to bind you to these Terms and you agree to be
                  bound by these Terms.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="font-bold underline">Licenses</span>
                </div>
                <ol className="list-decimal list-inside">
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">Limited License.</span>
                    Subject to your complete and ongoing compliance with these
                    Terms, Teamshares grants you, solely for your personal, use,
                    a limited, non-exclusive, non-transferable,
                    non-sublicensable, revocable license to access and use the
                    Site.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">
                      License Restrictions.
                    </span>
                    Except and solely to the extent such a restriction is
                    impermissible under applicable law, you may not: (a) use the
                    Site for any illegal purpose or in violation of any local,
                    state, national, or international law; (b) infringe the
                    Site, including reproduce, distribute, publicly display, or
                    publicly perform the Site; (c) make modifications to the
                    Site; (d) interfere with or circumvent any feature of the
                    Site or interfere with a users’ enjoyment of the Site,
                    including any security or access control mechanism, or (e)
                    reverse engineer or otherwise attempt to discover the source
                    code of any portion of the Site.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">
                      Information You Submit.
                    </span>
                    Do not submit any information or other materials that you
                    consider confidential or proprietary through the Site. If
                    you choose to provide input and suggestions regarding
                    problems with or proposed modifications or improvements to
                    the Site (<span className="font-bold">“Feedback”</span>),
                    then you do so on a non-confidential basis (regardless of
                    any designation or indication to the contrary in the
                    submitted information or any accompanying correspondence)
                    and you hereby grant Teamshares an unrestricted, perpetual,
                    irrevocable, non-exclusive, fully-paid, royalty-free
                    transferable, sublicensable right to exploit the Feedback in
                    any manner and for any purpose, including to improve the
                    Site and create other products and services.
                  </li>
                </ol>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">
                    Ownership; Proprietary Rights.
                  </span>
                  The Site is owned and operated by Teamshares. The visual
                  interfaces, graphics, design, compilation, information,
                  research reports, data, computer code (including source code
                  or object code), products, software, services, and all other
                  elements of the Site (
                  <span className="font-bold">“Materials”</span>) provided by
                  Teamshares are protected by intellectual property and other
                  laws. All Materials included in the Site are the property of
                  Teamshares or its third-party licensors. Except as expressly
                  authorized by Teamshares, you may not use the Materials.
                  Teamshares reserves all rights to the Materials not granted
                  expressly in these Terms.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">Linked Websites.</span>
                  The Site may contain links to third-party websites.
                  Third-party websites are not under Teamshares’s control, and
                  Teamshares is not responsible for their content.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">
                    Modification of these Terms.
                  </span>
                  We reserve the right to change these Terms on a going-forward
                  basis at any time. The most current version of these Terms is
                  in effect each time you access or use the Site. Except as
                  expressly permitted in this Section 6, the Terms by which the
                  parties are bound may be amended only by a written agreement
                  signed by authorized representatives of the parties to these
                  Terms. Disputes arising under these Terms will be resolved in
                  accordance with the version of these Terms that was in effect
                  at the time the dispute arose.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">
                    Modification of the Site.
                  </span>
                  Teamshares reserves the right to modify or discontinue the
                  Site at any time (including by limiting or discontinuing
                  certain features of the Site), temporarily or permanently,
                  without notice to you. Teamshares will have no liability for
                  any change to the Site or any suspension or termination of
                  your access to or use of the Site.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">Term and Termination.</span>
                  These Terms are in effect for any access or use by you of the
                  Site. If you violate any provision of these Terms: (a) you
                  must immediately cease all use of the Site; (b) your rights
                  under these Terms are terminated and you are no longer
                  authorized to access or use the Site; and (c) all definitions
                  and Sections 3(b), 3(c), 4, 6 through 12 will survive
                  termination.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="mr-1 font-bold">Indemnity.</span>
                  To the fullest extent permitted by law, you are responsible
                  for your use of the Site, and you will defend and indemnify
                  Teamshares and its officers, directors, employees,
                  consultants, affiliates, subsidiaries, and agents (together,
                  the <span className="font-bold">“Teamshares Entities”</span>)
                  from and against every claim brought by a third party, and any
                  related liability, damage, loss, and expense, including
                  reasonable attorneys’ fees and costs, arising out of or
                  connected with: (a) your unauthorized use of, or misuse of,
                  the Site; (b) your violation of any portion of these Terms or
                  any applicable law or regulation; (c) your violation of any
                  third-party right, including any intellectual property or
                  other proprietary right or right of publicity,
                  confidentiality, or privacy; or (d) any dispute or issue
                  between you and any third party. We reserve the right, at our
                  own expense, to assume the exclusive defense and control of
                  any matter otherwise subject to indemnification by you
                  (without limiting your indemnification obligations with
                  respect to that matter), and in that case, you agree to
                  cooperate with our defense of the applicable claims.
                </div>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="font-bold underline">
                    Disclaimers; No Warranties
                  </span>
                </div>
                <ol className="list-decimal list-inside">
                  <li type="a" className="mt-2 lg:mt-4">
                    THE SITE AND ALL MATERIALS AND CONTENT AVAILABLE THROUGH THE
                    SITE ARE PROVIDED “AS IS” AND ON AN “AS AVAILABLE” BASIS.
                    TEAMSHARES DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER
                    EXPRESS OR IMPLIED, RELATING TO THE SITE AND ALL MATERIALS
                    AND CONTENT AVAILABLE THROUGH THE SITE, INCLUDING: (A) ANY
                    IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, TITLE, QUIET ENJOYMENT, OR
                    NON-INFRINGEMENT; AND (B) ANY WARRANTY ARISING OUT OF COURSE
                    OF DEALING, USAGE, OR TRADE. TEAMSHARES DOES NOT WARRANT
                    THAT THE SITE OR ANY PORTION OF THE SITE, OR ANY MATERIALS
                    OR CONTENT OFFERED THROUGH THE SITE, WILL BE UNINTERRUPTED,
                    SECURE, OR FREE OF ERRORS, VIRUSES, OR OTHER HARMFUL
                    COMPONENTS, AND TEAMSHARES DOES NOT WARRANT THAT ANY OF
                    THOSE ISSUES WILL BE CORRECTED.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED
                    BY YOU FROM THE SITE, TEAMSHARES ENTITIES, OR ANY MATERIALS
                    OR CONTENT MADE AVAILABLE THROUGH THE SITE WILL CREATE ANY
                    WARRANTY THAT IS NOT EXPRESSLY STATED IN THESE TERMS OR A
                    SEPARATE WRITTEN AGREEMENT BETWEEN YOU AND TEAMSHARES. WE
                    ARE NOT RESPONSIBLE FOR ANY DAMAGE THAT MAY RESULT FROM THE
                    SITE OR YOUR ACCESS TO OR USE OF THE SITE. YOU UNDERSTAND
                    AND AGREE THAT YOU ACCESS AND USE THE SITE AT YOUR OWN
                    DISCRETION AND RISK, AND THAT WE ARE NOT RESPONSIBLE FOR ANY
                    DAMAGE TO YOUR PROPERTY (INCLUDING YOUR COMPUTER SYSTEM OR
                    MOBILE DEVICE USED IN CONNECTION WITH THE SITE) OR ANY LOSS
                    OF DATA.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    THE LIMITATIONS, EXCLUSIONS AND DISCLAIMERS IN THIS SECTION
                    APPLY TO THE FULLEST EXTENT PERMITTED BY LAW. TEAMSHARES
                    DOES NOT DISCLAIM ANY WARRANTY OR OTHER RIGHT THAT
                    TEAMSHARES IS PROHIBITED FROM DISCLAIMING UNDER APPLICABLE
                    LAW.
                  </li>
                </ol>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="font-bold underline">
                    Limitation of Liability
                  </span>
                </div>
                <ol className="list-decimal list-inside">
                  <li type="a" className="mt-2 lg:mt-4">
                    TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE
                    TEAMSHARES ENTITIES BE LIABLE TO YOU FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES
                    (INCLUDING DAMAGES FOR LOSS OF PROFITS, GOODWILL, OR ANY
                    OTHER INTANGIBLE LOSS) ARISING OUT OF OR RELATING TO YOUR
                    ACCESS TO OR USE OF, OR YOUR INABILITY TO ACCESS OR USE ANY
                    PORTION OF THE SITE, WHETHER BASED ON WARRANTY, CONTRACT,
                    TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL
                    THEORY, AND WHETHER OR NOT ANY TEAMSHARES ENTITY HAS BEEN
                    INFORMED OF THE POSSIBILITY OF DAMAGE.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    TO THE FULLEST EXTENT PERMITTED BY LAW, THE AGGREGATE
                    LIABILITY OF THE TEAMSHARES ENTITIES TO YOU FOR ALL CLAIMS
                    ARISING OUT OF OR RELATING TO THE USE OF OR ANY INABILITY TO
                    ACCESS OR USE ANY PORTION OF THE SITE OR OTHERWISE UNDER
                    THESE TERMS, WHETHER IN CONTRACT, TORT, OR OTHERWISE, IS
                    LIMITED TO $100.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    EACH PROVISION OF THESE TERMS THAT PROVIDES FOR A LIMITATION
                    OF LIABILITY, DISCLAIMER OF WARRANTIES, OR EXCLUSION OF
                    DAMAGES IS INTENDED TO AND DOES ALLOCATE THE RISKS BETWEEN
                    THE PARTIES UNDER THESE TERMS. THIS ALLOCATION IS AN
                    ESSENTIAL ELEMENT OF THE BASIS OF THE BARGAIN BETWEEN THE
                    PARTIES. EACH OF THESE PROVISIONS IS SEVERABLE AND
                    INDEPENDENT OF ALL OTHER PROVISIONS OF THESE TERMS. THE
                    LIMITATIONS IN THIS SECTION 11 WILL APPLY EVEN IF ANY
                    LIMITED REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
                  </li>
                </ol>
              </li>
              <li>
                <div className="mt-2 lg:mt-4">
                  <span className="font-bold underline">Miscellaneous</span>
                </div>
                <ol className="list-decimal list-inside">
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">General Terms.</span>
                    These Terms, together with the Privacy Policy available at:{" "}
                    <Link href="/privacy-policy" target="_blank" rel="noopener">
                      www.teamshares.com/privacy-policy
                    </Link>{" "}
                    (the <span className="font-bold">“Privacy Policy”</span>)
                    and any Additional Terms (defined below), are the entire and
                    exclusive understanding and agreement between you and
                    Teamshares regarding your use of the Site. You may not
                    assign or transfer these Terms or your rights under these
                    Terms, in whole or in part, by operation of law or
                    otherwise, without our prior written consent. We may assign
                    these Terms at any time without notice or consent. The
                    failure to require performance of any provision will not
                    affect our right to require performance at any other time
                    after that, nor will a waiver by us of any breach or default
                    of these Terms, or any provision of these Terms, be a waiver
                    of any subsequent breach or default or a waiver of the
                    provision itself. Use of section headers in these Terms is
                    for convenience only and will not have any impact on the
                    interpretation of any provision. Throughout these Terms the
                    use of the word “including” means “including but not limited
                    to”. If any part of these Terms is held to be invalid or
                    unenforceable, the unenforceable part will be given effect
                    to the greatest extent possible, and the remaining parts
                    will remain in full force and effect.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">Governing Law.</span>
                    These Terms are governed by the laws of the state of New
                    York without regard to conflict of law principles that would
                    result in the application of the laws of another
                    jurisdiction. You and Teamshares submit to the personal and
                    exclusive jurisdiction of the state courts and federal
                    courts located within Kings County, New York, for resolution
                    of any lawsuit or court proceeding permitted under these
                    Terms.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">Privacy Policy.</span>
                    Please read the{" "}
                    <Link href="/privacy-policy" target="_blank" rel="noopener">
                      Privacy Policy
                    </Link>{" "}
                    carefully for information relating to our collection, use,
                    storage, disclosure of your personal information. The{" "}
                    <Link href="/privacy-policy" target="_blank" rel="noopener">
                      Privacy Policy
                    </Link>{" "}
                    is incorporated by this reference into, and made a part of,
                    these Terms.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">Additional Terms.</span>
                    Your use of the Site is subject to all additional terms,
                    policies, rules, or guidelines applicable to the Site or
                    certain features of the Site that we may post on or link to
                    from the Site or that you agree to in connection with
                    certain technology or services made available to you by or
                    on behalf of Teamshares (the{" "}
                    <span className="font-bold">“Additional Terms”</span>). All
                    Additional Terms are incorporated by this reference into,
                    and made a part of, these Terms.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">Contact Information.</span>
                    The Site is offered by Teamshares Inc., located at 214
                    Sullivan Street, Suite 6B, NY, NY 10012. You may contact us
                    by sending correspondence to that address or by emailing us
                    at{" "}
                    <Link
                      href="mailto:support@teamshares.com"
                      target="_blank"
                      rel="noopener"
                    >
                      support@teamshares.com
                    </Link>
                    .
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">
                      Notice to California Residents.
                    </span>
                    If you are a California resident, under California Civil
                    Code Section 1789.3, you may contact the Complaint
                    Assistance Unit of the Division of Consumer Services of the
                    California Department of Consumer Affairs in writing at 1625
                    N. Market Blvd., Suite S-202, Sacramento, California 95834,
                    or by telephone at (800) 952-5210 in order to resolve a
                    complaint regarding the Site or to receive further
                    information regarding use of the Site.
                  </li>
                  <li type="a" className="mt-2 lg:mt-4">
                    <span className="mr-1 font-bold">International Use.</span>
                    The Site is intended for visitors located within the United
                    States. We make no representation that the Site is
                    appropriate or available for use outside of the United
                    States. Access to the Site from countries or territories or
                    by individuals where such access is illegal is prohibited.
                  </li>
                </ol>
              </li>
            </ol>
          </div>
        </main>
        <SimpleFooter />
      </div>
    </>
  );
};

export default TermsAndConditions;
